import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Lock, Loader2, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLogin() {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { username, password });
      localStorage.setItem("zx_admin_token", data.token);
      navigate("/admin/leads", { replace: true });
    } catch (err) {
      setError(
        err.response?.status === 401
          ? "Invalid username or password."
          : "Something went wrong. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/20 focus:border-slate-900 transition";

  return (
    <div data-testid="admin-login-page" className="min-h-screen flex items-center justify-center bg-slate-100 px-5">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-2 mb-6">
          <span className="h-10 w-10 rounded-2xl bg-slate-900 flex items-center justify-center">
            <ShieldCheck className="h-5 w-5 text-white" />
          </span>
          <span className="font-heading font-extrabold text-lg tracking-tight text-slate-900">
            Zentryx <span className="text-slate-400">Admin</span>
          </span>
        </div>
        <form
          onSubmit={submit}
          data-testid="admin-login-form"
          className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm"
        >
          <h1 className="font-heading text-2xl font-bold text-slate-900 mb-1">Sign in</h1>
          <p className="text-sm text-slate-500 mb-6">Internal leads dashboard — authorized access only.</p>

          <label className="block text-xs uppercase tracking-widest font-semibold text-slate-400 mb-1.5">Username</label>
          <input
            data-testid="admin-username-input"
            className={inputCls}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="username"
          />

          <label className="block text-xs uppercase tracking-widest font-semibold text-slate-400 mb-1.5 mt-4">Password</label>
          <input
            data-testid="admin-password-input"
            type="password"
            className={inputCls}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
          />

          {error && (
            <p data-testid="admin-login-error" className="mt-4 text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-2.5">
              {error}
            </p>
          )}

          <button
            data-testid="admin-login-submit"
            type="submit"
            disabled={loading}
            className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-slate-900 hover:bg-slate-800 disabled:opacity-70 text-white font-semibold py-3.5 transition-colors"
          >
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Lock className="h-4 w-4" />}
            {loading ? "Signing in..." : "Sign in"}
          </button>
        </form>
      </div>
    </div>
  );
}
